import { useEffect, useMemo, useState } from "react";
import { Check, Plus, Search, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

/**
 * Searchable list of the current user's friends with a tap-to-add action.
 * Used both at group-creation time (controlled, returns picked ids/array)
 * and inside an existing group (uncontrolled, posts to API on tap).
 *
 * Props:
 * - existingMemberIds: array of user_ids already in the group (renders disabled "Added")
 * - picked: array of user_ids currently selected (for create-mode)
 * - onTogglePick(friend): called in create-mode
 * - onAddInstant(friend): called in existing-group mode (returns Promise to disable while pending)
 * - mode: "pick" | "add"
 */
export const FriendPicker = ({ existingMemberIds = [], picked = [], onTogglePick, onAddInstant, mode = "pick" }) => {
  const [friends, setFriends] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/friends");
        setFriends(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return friends;
    return friends.filter((f) => f.name.toLowerCase().includes(term) || f.email.toLowerCase().includes(term));
  }, [friends, q]);

  const handleClick = async (f) => {
    if (mode === "pick") {
      onTogglePick?.(f);
      return;
    }
    if (existingMemberIds.includes(f.id)) return;
    setBusy(f.id);
    try { await onAddInstant?.(f); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="friend-picker">
      <div className="relative">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search friends…"
          className="pl-9 bg-muted border-border text-base"
          data-testid="friend-picker-search"
        />
      </div>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-border bg-foreground/[0.02] divide-y divide-border">
        {loading ? (
          <div className="py-6 text-center text-xs text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-6 px-4 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
            <Users className="h-4 w-4" strokeWidth={1.5} />
            {q ? "No matches" : "No friends yet — add some from the Friends page."}
          </div>
        ) : (
          filtered.map((f) => {
            const isMember = existingMemberIds.includes(f.id);
            const isPicked = picked.includes(f.id);
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => handleClick(f)}
                disabled={isMember || busy === f.id}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left transition ${
                  isMember ? "opacity-50 cursor-not-allowed" : "hover:bg-foreground/5"
                }`}
                data-testid={`friend-pick-${f.id}`}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <span className="h-8 w-8 rounded-full bg-foreground/5 border border-border flex items-center justify-center text-base shrink-0">{f.avatar_emoji || "🙂"}</span>
                  <span className="min-w-0">
                    <span className="block text-sm truncate">{f.name}</span>
                    <span className="block text-[11px] font-mono text-muted-foreground truncate">{f.email}</span>
                  </span>
                </span>
                <span className="shrink-0">
                  {isMember ? (
                    <span className="text-[10px] font-mono uppercase tracking-wider brand-text flex items-center gap-1"><Check className="h-3 w-3" strokeWidth={2} />Added</span>
                  ) : isPicked ? (
                    <span className="text-[10px] font-mono uppercase tracking-wider brand-text flex items-center gap-1"><Check className="h-3 w-3" strokeWidth={2} />Picked</span>
                  ) : (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Plus className="h-3 w-3" strokeWidth={2} />Add</span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
